import { setAtPath } from "@quintara/site-kit";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ROUTES } from "../common/constants";
import { useAuth } from "../contexts/AuthContext";
import { JsonEditor } from "../editor/JsonEditor";
import { PAGE_TITLE } from "../pages-meta";
import { PagePreview } from "../preview/PagePreview";
import { getPageContent, publishPage, saveDraft } from "../services/Api";
import { PAGE_KIT } from "../siteKitMap";
import { Loader } from "../ui/Loader";

type Status = "loading" | "ready" | "saving" | "publishing";

function errMessage(e: unknown): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Something went wrong.";
}

export function PageEditor() {
  const { slug = "" } = useParams();
  const { admin } = useAuth();
  const entry = PAGE_KIT[slug];
  const [content, setContent] = useState<Record<string, unknown> | null>(null);
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<"preview" | "form">("preview");

  // Navigating preview→preview reuses this component, so `content` briefly holds
  // the previous page's data while the new page's renderer is already mounted —
  // which would crash (e.g. SolutionsSections reading `specs`). Only treat
  // content as usable once it was loaded for the slug now in the URL.
  const ready = content !== null && loadedSlug === slug;

  // WYSIWYG: a field edited directly in the preview writes back into content
  // at its dot-path, keeping the preview and the saved draft in sync.
  function handleInlineEdit(path: string, value: string) {
    setContent((prev) => (prev ? setAtPath(prev, path, value) : prev));
  }

  useEffect(() => {
    if (!entry) return;
    let active = true;
    setStatus("loading");
    getPageContent(slug, "draft")
      .then((r) => {
        if (!active) return;
        setContent((r.data.data as Record<string, unknown>) ?? (structuredClone(entry.default) as Record<string, unknown>));
        setLoadedSlug(slug);
        setStatus("ready");
      })
      .catch(() => {
        if (!active) return;
        setContent(structuredClone(entry.default) as Record<string, unknown>);
        setLoadedSlug(slug);
        setStatus("ready");
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function onSave() {
    if (!content) return;
    setStatus("saving");
    setMessage(null);
    try {
      await saveDraft(slug, content);
      setMessage("Draft saved.");
    } catch (e) {
      setMessage(errMessage(e));
    } finally {
      setStatus("ready");
    }
  }

  async function onPublish() {
    if (!content) return;
    setStatus("publishing");
    setMessage(null);
    try {
      await saveDraft(slug, content);
      await publishPage(slug);
      setMessage("Published — live on the site.");
    } catch (e) {
      setMessage(errMessage(e));
    } finally {
      setStatus("ready");
    }
  }

  if (!entry) {
    return (
      <div className="page">
        <p className="error-banner">Unknown page: {slug}</p>
        <Link to={ROUTES.PAGES}>← Back to pages</Link>
      </div>
    );
  }

  return (
    <div className="editor">
      <div className="editor-top">
        <div>
          <Link to={ROUTES.PAGES} className="back-link">
            ← Pages
          </Link>
          <h1>{PAGE_TITLE[slug] ?? slug}</h1>
        </div>
        <div className="editor-actions">
          {admin?.role === "admin" && (
            <Link to={ROUTES.PAGE_HISTORY(slug)} className="btn-ghost">
              History
            </Link>
          )}
          <button
            className="btn-ghost"
            onClick={() => {
              setMode((m) => (m === "form" ? "preview" : "form"));
              setEditing(false);
            }}
            disabled={!content}
          >
            {mode === "form" ? "◧ Preview" : "☰ Fields"}
          </button>
          {mode === "preview" && (
            <button
              className={editing ? "btn-primary" : "btn-ghost"}
              onClick={() => setEditing((e) => !e)}
              disabled={!content}
            >
              {editing ? "Done editing" : "✎ Edit on page"}
            </button>
          )}
          <button className="btn-ghost" onClick={onSave} disabled={!content || status === "saving"}>
            {status === "saving" ? "Saving…" : "Save draft"}
          </button>
          <button className="btn-primary" onClick={onPublish} disabled={!content || status === "publishing"}>
            {status === "publishing" ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      {message && <p className="editor-message">{message}</p>}
      {!ready && <Loader label="Loading page…" />}

      {ready && editing && mode === "preview" && (
        <p className="edit-hint">✎ Click any text on the page to edit it. Press Enter to commit, Esc to cancel.</p>
      )}

      {ready && content && (
        <div className="editor-stage">
          {mode === "form" ? (
            <div className="editor-form editor-form-solo">
              <JsonEditor value={content} onChange={(v) => setContent(v as Record<string, unknown>)} />
            </div>
          ) : (
            <div className="preview-pane">
              <PagePreview slug={slug} content={content} editing={editing} onEdit={handleInlineEdit} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
